import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { InputNumber } from 'antd';
import { IDiscountReasonCode } from '../../../logic/Models/Responses/DiscountReasonCodeResponse';
import { IPlanHybridDiscount } from '../../../logic/Models/Responses/CompetitiveAllowanceResponse';
import { Dropdown } from '../../../components/Dropdown/Dropdown';
import { IOptionItem } from '../../../components/Dropdown/IOptionItem';
import { RoundAcres } from '../../../logic/Utility/CalculationUtilities';
import { cornId } from '../../../logic/store/Seeds/CropsSlice';
import { backgroundColor } from 'styled-system';
import { ProductLevelDiscountRow } from './ProductLevelDiscountRow';

const NumberInput = styled(InputNumber)`
	width: 100px;
	height: 25px;
	font-size: ${themeGet('fontSizes.normal')};
	margin-right: -2px;
	border: thin solid ${themeGet('colors.lightestGrey')};
	&:focus:not(:disabled),
	&:hover:not(:disabled),
	&:active:not(:disabled) {
		border-color: black;
		background-color: ${themeGet('colors.white')};
	}
	.ant-input-number-handler-wrap
	{
		display: none;
	}
	.ant-input-number-input {
		text-align: right;
		height: auto;
		padding-right: 3px;
	}
`;
export interface IHybridDiscountRowProps
{
	isReadOnly: boolean;
	reasonCodes: IDiscountReasonCode[];
	hybridLevelDiscountItem: IPlanHybridDiscount;
	totalCropLevelDiscount: number;
	updateHybridRow: (hybridDiscountItem: IPlanHybridDiscount) => void;
}

export const HybridLevelDiscountRow = (props: IHybridDiscountRowProps) =>
{
	const { 
		isReadOnly,
		hybridLevelDiscountItem,
		reasonCodes,
		totalCropLevelDiscount,
		updateHybridRow,
	} = props;

	const theme = useTheme();

	const [reasonOptions, setReasonOptions] = useState<IOptionItem[]>([]);
	const minimumPricePerBag = Math.min(...hybridLevelDiscountItem.Products.map(p => p.PricePerBag));
	const totalMsrp = hybridLevelDiscountItem.Products.reduce((acc, p) => acc + (p.PricePerBag * p.Quantity), 0);
	const totalDiscount = hybridLevelDiscountItem.Products.reduce((acc, p) => acc + ((totalCropLevelDiscount + hybridLevelDiscountItem.PerBagDiscount) * p.Quantity), 0);
	const totalPriceAfterDiscount = totalMsrp - totalDiscount;

	useEffect(() =>
	{
		if (reasonCodes)
		{
			const createReasonCodeOptions = [];

			// Add an empty value
			createReasonCodeOptions.push(
				{
					label: 'Select a Reason',
					value: ''
				});
			
			reasonCodes.forEach(reasonItem =>
			{
				const brandOption: IOptionItem =
				{
					label: reasonItem.Code,
					value: reasonItem.Id
				};
		
				createReasonCodeOptions.push(brandOption);
			});
		
			setReasonOptions([...createReasonCodeOptions]);
		}
	},[reasonCodes]);

	const onChangeReason = (value: string) =>
	{
		if (value === hybridLevelDiscountItem.DiscountReasonCode)
		{
			// Don't update if there is no change
			return;
		}
		
		updateHybridRow({ ...hybridLevelDiscountItem, DiscountReasonCode: value });
	};
	
	
	const onChangeDiscount = (value: number) =>
	{
		if (value === hybridLevelDiscountItem.PerBagDiscount)
		{
			// Don't update if there is no change
			return;
		}

		if (value < 0)
		{
			value = 0;
		}

		// The maximum discount is the minimum price per bag minus the total crop level discount.
		// If the discount is greater than this, we need to cap it.
		// This is to ensure that the discount does not exceed the minimum price per bag for any product in the hybrid.
		
		if ((value + totalCropLevelDiscount) > minimumPricePerBag)
		{
			value = minimumPricePerBag - totalCropLevelDiscount;
		}
		
		updateHybridRow({...hybridLevelDiscountItem, PerBagDiscount: value });
	};

	return (
		<>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					marginTop: 5,
					paddingRight: 15,
					paddingLeft: 15,
					paddingTop: 5,
					paddingBottom: 7,
					borderStyle: 'solid',
					borderRadius: 4,
					borderWidth: 1,
					borderBottomWidth: 0,
					borderColor: theme.colors.lightGrey,
					width: '100%',
					fontWeight: theme.fontWeights.bold,
					fontSize: theme.fontSizes.small,
					textAlign: 'right', 
					backgroundColor: theme.colors.lightestGrey
				}}>
				<div style={{ display: 'flex', flexDirection: 'column', width: '20%', textAlign: 'left' }}>
					{`${hybridLevelDiscountItem.HybridName} ${hybridLevelDiscountItem.TraitIdentityName}`}
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', width: '5%' }}>
					<div>{RoundAcres(hybridLevelDiscountItem.Acres)}</div>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', width: '10%' }}/>
				<div style={{ display: 'flex', flexDirection: 'column', width: '5%' }}>
					<div>{hybridLevelDiscountItem.Quantity}</div>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', width: '10%' }}>
					<div>{Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalMsrp)}</div>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', width: '15%' }}>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
						<div style={{ marginRight: 5 }}>{isReadOnly ? '' : '$'}</div>
						{
							isReadOnly ? 
								<div>{hybridLevelDiscountItem.PerBagDiscount !== undefined ? 
									Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(hybridLevelDiscountItem.PerBagDiscount)
									: '$0.00'}</div>
								:
								<NumberInput
									style={{
										width: '75%',
										fontSize: theme.fontSizes.small,
										top: 1,
										borderColor: hybridLevelDiscountItem.PerBagDiscount === 0 
									&& !!hybridLevelDiscountItem.DiscountReasonCode ? theme.colors.availabilityRed : theme.colors.lightestGrey,
									}}
									className='CropLevel_Discount'
									value={hybridLevelDiscountItem.PerBagDiscount !== undefined ? hybridLevelDiscountItem.PerBagDiscount : ''}
									step={0.05}
									min={0}
									max={minimumPricePerBag}
									onBlur={(evt: React.FocusEvent<HTMLInputElement>) =>
										onChangeDiscount(evt.currentTarget.value ? Number(evt.currentTarget.value) : hybridLevelDiscountItem.PerBagDiscount)}
									onPressEnter={(evt: React.KeyboardEvent<HTMLInputElement>) => 
										onChangeDiscount(evt.target && (evt.target as HTMLInputElement).value ? 
											Number((evt.target as HTMLInputElement).value) : hybridLevelDiscountItem.PerBagDiscount)}
								/>
						}
					</div>
				</div>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						width: '25%',
						alignItems: 'flex-end'
					}}
				>
					{
						isReadOnly ?
							<div>{reasonCodes.find(c => c.Id === hybridLevelDiscountItem.DiscountReasonCode)?.Code ?? 'None'}</div>
							:
							<Dropdown
								className='CropLevel_ReasonCode'
								variant='outlinednarrownopad'
								options={reasonOptions}
								onSelect={onChangeReason}
								selected={hybridLevelDiscountItem.DiscountReasonCode}
								containerWidth={'80%'}
								titleFontSize={theme.fontSizes.extraSmall}
								relativePosition={true}
								showErrorBorder={hybridLevelDiscountItem.PerBagDiscount > 0 && (!hybridLevelDiscountItem.DiscountReasonCode || hybridLevelDiscountItem.DiscountReasonCode === '')}
								errorBorderColor={theme.colors.availabilityRed}
							/>
					}
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', width: '12%' }}>
					<div>{Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalDiscount)}</div>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', width: '10%' }}>
					<div>{Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalPriceAfterDiscount)}</div>
				</div>
			</div>
			{
				hybridLevelDiscountItem.Products.map((product) => (
					<ProductLevelDiscountRow
						key={`${hybridLevelDiscountItem.HybridId}_${product.TreatmentId}`}
						perBagDiscount={hybridLevelDiscountItem.PerBagDiscount + totalCropLevelDiscount}
						product={product}
					/>
				))
			}
		</>
	);
};

